// client/src/components/Routing/TerritoryWorkbench/WeekViz/WeekSidebar.js
import { useMemo, useState } from 'react'
import { WEEKDAYS, WEEKDAY_COLORS } from './weekColors'
import { parseYmd } from '../scheduleUtils'
import { BLOCK_COLORS, blockSegments, formatTimeFromMinutes } from './weekDayBars'

function formatDateShort (dateStr) {
  if (!dateStr) return ''
  const d = parseYmd(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// "Mon Jun 1" — used in the missing-detail list.
function formatDayLabel (dateStr) {
  if (!dateStr) return ''
  const d = parseYmd(dateStr)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/**
 * Sidebar shows one row per (weekType, dayIndex). When multiple crews share a
 * day (fleet mode), the upstream `days[]` has one entry per crew-day — merge
 * them so the user sees a single Mon/Tue/... per week, not Mon×N.
 */
function mergeDaysByCalendar (days) {
  const byKey = new Map()
  for (const d of days) {
    const key = `${d.weekType || 'A'}|${d.dayIndex}`
    let m = byKey.get(key)
    if (!m) {
      m = {
        dayIndex: d.dayIndex,
        weekType: d.weekType,
        date: d.date,
        startMin: d.startMin,
        endMin: d.endMin,
        blocks: [],
        totals: { stopCount: 0, workMin: 0, driveMin: 0, totalMin: 0 },
        _areaCounts: new Map()
      }
      byKey.set(key, m)
    }
    if (d.startMin != null) m.startMin = m.startMin == null ? d.startMin : Math.min(m.startMin, d.startMin)
    if (d.endMin != null) m.endMin = m.endMin == null ? d.endMin : Math.max(m.endMin, d.endMin)
    m.totals.stopCount += d.totals?.stopCount || 0
    m.totals.workMin   += d.totals?.workMin   || 0
    m.totals.driveMin  += d.totals?.driveMin  || 0
    m.totals.totalMin  += d.totals?.totalMin  || 0
    for (const b of d.blocks || []) {
      m.blocks.push(b)
    }
    if (d.areaName) {
      m._areaCounts.set(d.areaName, (m._areaCounts.get(d.areaName) || 0) + (d.totals?.stopCount || 1))
    }
  }
  // Collapse blocks by kind so the bar shows aggregate composition, not N stacks.
  for (const m of byKey.values()) {
    const byKind = new Map()
    for (const b of m.blocks) {
      byKind.set(b.kind, (byKind.get(b.kind) || 0) + (b.min || 0))
    }
    m.blocks = Array.from(byKind, ([kind, min]) => ({ kind, min }))
    // Pick the dominant area (highest stop weight).
    let bestArea = null, bestCount = 0
    for (const [name, count] of m._areaCounts) {
      if (count > bestCount) { bestArea = name; bestCount = count }
    }
    m.areaName = bestArea
    delete m._areaCounts
  }
  // Sort: week A before week B, then Mon..Fri.
  return Array.from(byKey.values()).sort((a, b) => {
    const wa = (a.weekType || 'A') === 'A' ? 0 : 1
    const wb = (b.weekType || 'A') === 'A' ? 0 : 1
    if (wa !== wb) return wa - wb
    return a.dayIndex - b.dayIndex
  })
}

function ExpectedDayBar ({ blocks, startMin, endMin }) {
  const segments = blockSegments(blocks)
  const totalMin = endMin - startMin
  const totalHoursLabel = totalMin > 0 ? `${(totalMin / 60).toFixed(1)}h` : ''

  if (segments.length === 0) {
    return (
      <div style={{
        height: 10, borderRadius: 4, marginTop: 6,
        background: 'transparent',
        borderTop: '1px dotted var(--df-line, #e0d8c2)'
      }} />
    )
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        display: 'flex', height: 10, borderRadius: 4, overflow: 'hidden',
        background: 'var(--df-line, #ede6d2)'
      }}>
        {segments.map((seg, i) => (
          <div key={i} style={{
            width: `${seg.widthPct}%`, background: seg.color, height: '100%'
          }} title={`${seg.kind}: ${seg.min}m`} />
        ))}
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', marginTop: 2
      }}>
        <span>{formatTimeFromMinutes(startMin)}</span>
        <span style={{ color: 'var(--df-ink-3, #6f7a6b)', fontWeight: 600 }}>{totalHoursLabel}</span>
        <span>{formatTimeFromMinutes(endMin)}</span>
      </div>
    </div>
  )
}

function LegendChip ({ kind, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, marginRight: 10 }}>
      <span style={{ width: 9, height: 9, background: BLOCK_COLORS[kind], borderRadius: 2 }} />
      {label}
    </span>
  )
}

export default function WeekSidebar ({
  focusCrewId, isFleetMode, availableCrews,
  days, missingDates, missingByCrew,
  activeDay, setActiveDay, setHoverDay
}) {
  // Each day's date is provided by the server payload; we no longer need focusDate.
  // Collapse per-crew day rows into one row per calendar day (fleet mode
  // otherwise yields N×Mon, N×Tue, …).
  const mergedDays = useMemo(() => mergeDaysByCalendar(days || []), [days])
  // Aggregate sidebar header totals from the merged set so we don't double-count.
  const totalStops = mergedDays.reduce((a, d) => a + (d.totals?.stopCount || 0), 0)
  const totalSvcHours = Math.round(mergedDays.reduce((a, d) => a + (d.totals?.workMin || 0), 0) / 60)

  const scopeLabel = isFleetMode
    ? `All crews (${availableCrews?.length || 0})`
    : (availableCrews?.find(c => String(c.crewId) === String(focusCrewId))?.crewName || 'Crew')

  // Real crew-day count = sum of empty dates across all crews (not distinct
  // dates). Falls back to the flat distinct-date count when the server is on
  // the older payload shape.
  const crewDayCount = (missingByCrew && missingByCrew.length)
    ? missingByCrew.reduce((a, c) => a + c.dates.length, 0)
    : (missingDates?.length || 0)
  const errorCount = (missingByCrew || []).reduce(
    (a, c) => a + c.dates.filter(d => d.reason === 'error').length, 0
  )
  const [showMissingDetail, setShowMissingDetail] = useState(false)

  return (
    <div style={{
      width: 312, flexShrink: 0, borderLeft: '1px solid #e0d8c2',
      background: '#fbf8f1', display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ padding: '15px 16px 13px', borderBottom: '1px solid #ede6d2' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: '#1c2419' }}>The week at a glance</div>
        <div style={{ fontSize: 11.5, color: '#6f7a6b', marginTop: 2 }}>
          {scopeLabel} · {totalStops} stops · {totalSvcHours}h service
        </div>
        {crewDayCount > 0 && (
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => setShowMissingDetail(s => !s)}
              title="Show which crews/days have no live route data"
              style={{
                fontSize: 11, color: errorCount > 0 ? '#a9341d' : '#8c5b00',
                background: errorCount > 0 ? '#fbe3dc' : '#fdf4dc',
                border: 'none', borderRadius: 6, padding: '4px 8px',
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer'
              }}
            >
              <span>
                {crewDayCount} crew-day{crewDayCount === 1 ? '' : 's'} with no schedule
                {errorCount > 0 ? ` · ${errorCount} fetch error${errorCount === 1 ? '' : 's'}` : ''}
              </span>
              <span style={{ fontSize: 9, opacity: 0.7 }}>{showMissingDetail ? '▴' : '▾'}</span>
            </button>
            {showMissingDetail && (
              <div style={{
                marginTop: 6, background: '#fffaf0', border: '1px solid #ede0bd',
                borderRadius: 6, padding: '6px 8px', fontSize: 11, color: '#3d463a',
                maxHeight: 200, overflowY: 'auto'
              }}>
                {(missingByCrew || []).length === 0 && (
                  <div style={{ color: '#6f7a6b' }}>
                    {(missingDates || []).map(d => formatDayLabel(d)).join(' · ') || 'No detail available'}
                  </div>
                )}
                {(missingByCrew || []).map(c => (
                  <div key={c.crewId} style={{ marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, color: '#1c2419' }}>{c.crewName}</div>
                    <div style={{ color: '#6f7a6b', marginLeft: 2 }}>
                      {c.dates.map((d, i) => (
                        <span key={d.date}>
                          {i > 0 ? ' · ' : ''}
                          <span style={{
                            color: d.reason === 'error' ? '#a9341d' : '#6f7a6b'
                          }} title={d.reason === 'error' ? d.message : 'No schedule found in Aspire'}>
                            {formatDayLabel(d.date)}
                            {d.reason === 'error' ? ' ⚠' : ''}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{
                  marginTop: 6, paddingTop: 6, borderTop: '1px solid #ede0bd',
                  color: '#97a094', fontSize: 10.5, lineHeight: 1.4
                }}>
                  "No schedule" = nothing scheduled in Aspire for that crew-day.
                  {errorCount > 0 ? ' "⚠" = Aspire fetch failed — try the refresh button.' : ''}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expected-day legend */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #ede6d2', background: '#faf6ee' }}>
        <div style={{
          fontSize: 10, fontWeight: 800, color: '#97a094',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4
        }}>Expected day</div>
        <div>
          <LegendChip kind="work" label="Work" />
          <LegendChip kind="drive" label="Drive" />
          <LegendChip kind="break" label="Break" />
          <LegendChip kind="lunch" label="Lunch" />
        </div>
      </div>

      {/* Per-day list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 14px' }}>
        {mergedDays.map((d, i) => {
          const on = activeDay === d.dayIndex
          const fade = activeDay != null && !on
          const empty = (d.totals?.totalMin || 0) === 0
          return (
            <div
              key={`${d.date}-${i}`}
              onClick={() => setActiveDay(on ? null : d.dayIndex)}
              onMouseEnter={() => setHoverDay(d.dayIndex)}
              onMouseLeave={() => setHoverDay(null)}
              style={{
                padding: '10px 4px', borderBottom: '1px solid #ede6d2',
                cursor: 'pointer', opacity: fade ? 0.5 : 1, transition: 'opacity 0.13s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <span style={{
                  width: 11, height: 11, borderRadius: '50%',
                  background: WEEKDAY_COLORS[d.dayIndex], flexShrink: 0,
                  border: '2px solid #fff', boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                }} />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1c2419' }}>
                    {WEEKDAYS[d.dayIndex]?.full}
                  </span>
                  <span style={{ fontSize: 11, color: '#97a094' }}>{formatDateShort(d.date)}</span>
                </div>
              </div>
              <ExpectedDayBar blocks={d.blocks || []} startMin={d.startMin} endMin={d.endMin} />
              <div style={{ fontSize: 11, color: '#6f7a6b', marginTop: 4 }}>
                {empty
                  ? <i>No work scheduled</i>
                  : (
                    <>
                      <b style={{ color: '#1c2419' }}>{d.totals.stopCount}</b> stops
                      · {(d.totals.workMin / 60).toFixed(1)}h work
                      · {(d.totals.driveMin / 60).toFixed(1)}h drive
                      {d.areaName ? ` · ${d.areaName}` : ''}
                    </>
                  )
                }
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
